import React, { useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { Badge } from "src/components/ui/Badge";
import { Edit, BookOpen, Plus, Trash2, X } from "lucide-react";
import { Modal } from "src/components/ui/Modal";
import { FormInput } from "src/components/ui/FormInput";
import { TutorExtendedProfileData, TutorSectionType } from "src/interfaces/TutorProfile";
import { useTutorProfileEditStore } from "src/store/tutorProfileEditStore";
import { useActiveSubjects } from "src/hooks/useSubjects";
import ChipInput from "src/components/ui/ChipInput";

interface TutorSubjectsSectionProps {
  profileData: TutorExtendedProfileData;
  editData: Partial<TutorExtendedProfileData>;
  activeSectionEdit: TutorSectionType | null;
  setActiveSectionEdit: (section: TutorSectionType | null) => void;
  updateEditData: (field: string, value: any) => void;
  updateProfile: (data: Partial<TutorExtendedProfileData>) => Promise<void>;
  isModified?: boolean;
}

const TutorSubjectsSection: React.FC<TutorSubjectsSectionProps> = ({
  profileData,
  editData,
  activeSectionEdit,
  setActiveSectionEdit,
  updateEditData,
  updateProfile,
  isModified = false
}) => {
  const {
    editingSubjects,
    setEditingSubjects,
    addSubject,
    removeSubject,
    updateSubject
  } = useTutorProfileEditStore();

  // Fetch subjects data to map IDs to names
  const { data: subjectsData } = useActiveSubjects();

  // Create a mapping from subject ID to subject name
  const subjectIdToName = useMemo(() => {
    if (!subjectsData) return {};
    return subjectsData.reduce((acc, subject) => {
      acc[subject.id] = subject.name;
      return acc;
    }, {} as Record<string, string>);
  }, [subjectsData]);

  // Since subjects are now stored as names, we can use them directly
  const getSubjectNames = (subjects: string[]) => {
    return subjects.filter(Boolean);
  };

  // Sync subjects with updateEditData whenever editingSubjects changes
  useEffect(() => {
    if (activeSectionEdit === 'subjects') {
      updateEditData("subjects_taught", editingSubjects);
    }
  }, [editingSubjects, activeSectionEdit, updateEditData]);
  
  return (
    <>
      {/* Approved Subjects Section */}
      <Card className={isModified ? "ring-2 ring-blue-200 bg-blue-50/30" : ""}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center">
            <BookOpen className="h-5 w-5 mr-2" />
            Approved Subjects
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 p-0 rounded-full hover:bg-gray-100"
            onClick={() => {
              const currentSubjects = editData.subjects_taught || profileData.subjects_taught;
              setEditingSubjects(currentSubjects);
              setActiveSectionEdit('subjects');
            }}
            aria-label="Edit Approved Subjects"
          >
            <Edit className="h-4 w-4 text-gray-500" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {profileData.subjects_taught && profileData.subjects_taught.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {getSubjectNames(profileData.subjects_taught).map((subjectName, index) => (
                  <span
                    key={index}
                    className="bg-blue-100 text-blue-700 rounded-full px-4 py-1 font-semibold text-base"
                  >
                    {subjectName}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No approved subjects specified</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Subjects Edit Modal */}
      <Modal
        isOpen={activeSectionEdit === 'subjects'}
        onClose={() => setActiveSectionEdit(null)}
        title="Edit Approved Subjects"
      >
        <div className="space-y-4">
          <div className="text-sm text-gray-500">
            List the subjects you are approved to teach on the platform.
          </div>

          {/* Selected Subjects Display */}
          {editingSubjects.length > 0 && (
            <div className="space-y-3">
              <div className="text-sm font-medium text-gray-700">Selected Subjects:</div>
              <div className="flex flex-wrap gap-2">
                {editingSubjects.map((subject, index) => (
                  <Badge 
                    key={index} 
                    className="flex items-center gap-1 pr-1 bg-blue-100 text-blue-800 border border-blue-300 font-semibold px-3 py-1 rounded-full shadow-sm"
                  >
                    <span>{subject}</span>
                    <button
                      type="button"
                      className="ml-1 p-0.5 rounded hover:bg-blue-200"
                      onClick={() => removeSubject(index)}
                      title="Remove subject"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Add New Subject */}
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">Add Subject:</div>
            <ChipInput
              value={null}
              onChange={val => {
                let subjectValue = "";
                if (Array.isArray(val)) {
                  subjectValue = val[0]
                    ? typeof val[0] === "object" && "value" in val[0]
                      ? val[0].value
                      : String(val[0])
                    : "";
                } else if (val && typeof val === "object" && "value" in val) {
                  subjectValue = val.value;
                } else if (typeof val === "string") {
                  subjectValue = val;
                }
                
                if (subjectValue && !editingSubjects.includes(subjectValue)) {
                  addSubject();
                  updateSubject(editingSubjects.length, subjectValue);
                }
              }}
              options={(subjectsData || [])
                .map(s => ({ label: s.name, value: s.name }))
                .filter(opt => !editingSubjects.includes(opt.value))}
              multi={false}
              placeholder="Select a subject to add..."
              getOptionLabel={opt => typeof opt === 'string' ? opt : opt.label}
              getOptionValue={opt => typeof opt === 'string' ? opt : opt.value}
              allowCustom={false}
            />
          </div>

          {editingSubjects.length === 0 && (
            <div className="text-center py-4 text-gray-500">
              <BookOpen className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">No subjects selected yet</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setActiveSectionEdit(null)}>
              Cancel
            </Button>
            <Button onClick={() => {
              updateProfile(editData);
              setActiveSectionEdit(null);
            }}>
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TutorSubjectsSection;